import { DbRef, DUMMY_PLACE_GROUP_ID, SamplesDeliveryType, CollectionDate, CollectionTime, orderedMedicalTitleRanking } from '@caresend/types';
import dayjs from 'dayjs';
import { Holidays } from 'us-holidays';
import { getLatestSupplyArrivalTimestamp } from './supply.js';
import { getUnknownErrorMessage } from '../error/errorHandling.js';
import { changeTimeZone, getDayOfWeekFromDayJS, timeInMsToHoursAndMinutes } from '../formatters/date.js';
import { HOUR, MINUTE } from '../initializers/date.js';
import { deduplicateArray } from '../languageExtensions/array.js';
import { objectFilter, objectSome } from '../languageExtensions/object.js';
import { nullishFilter, isNullish } from '../typeValidation/nullish.js';

const SAMPLE_STABILITY_HOURS = 72;
const SUNDAY = 0;
const FRIDAY = 5;
const SATURDAY = 6;
const getTimeSlotPath = (timeSlot, childPath) => `${DbRef.TIME_SLOTS_BY_SERVICE_REGION}/${timeSlot.serviceRegionID}` + `/${timeSlot.startDate}/${timeSlot.startTime}` + (childPath ? `/${childPath}` : '');

const getAllowedMedicalTitles = minimumTitle => {
  const titleIndex = orderedMedicalTitleRanking.indexOf(minimumTitle);
  return orderedMedicalTitleRanking.slice(undefined, titleIndex + 1);
};

const doesNurseMeetOfficeAffiliation = (nurse, draftWaypoint) => {
  const {
    officeID,
    createdByOfficeID
  } = draftWaypoint;
  const {
    allOfficeAffiliations,
    officeAffiliations
  } = nurse;
  if (allOfficeAffiliations) return true;
  if (officeID && (officeAffiliations === null || officeAffiliations === void 0 ? void 0 : officeAffiliations[officeID])) return true;
  if (createdByOfficeID && (officeAffiliations === null || officeAffiliations === void 0 ? void 0 : officeAffiliations[createdByOfficeID])) return true;
  return false;
};
/**
 * Returns true if the nurse meets the offer deadline. If the offer deadline
 * is in the past, the nurse is not eligible to fill the visit.
 */


const doesNurseMeetOfferDeadline = (nurse, timeSlotStartTime) => {
  const {
    offerDeadline
  } = nurse;
  if (offerDeadline === 0) return true;
  return timeSlotStartTime - Date.now() >= offerDeadline;
};
/**
 * Returns true if the nurse meets the following criteria:
 * - Active
 * - Owns a centrifuge if required
 * - Is not a pending nurse if not allowed
 * - Has the required medical title
 * - Is affiliated with the required offices
 * - Meets the offer deadline
 */


const isValidNurse = (nurse, draftWaypoint, timeSlotStartTime, requiresCentrifuge, usePendingNurseID, allowedTitles) => {
  const {
    active,
    ownsCentrifuge,
    nurseID,
    medicalTitle
  } = nurse;
  if (!active) return false;
  const missingCentrifuge = requiresCentrifuge && !ownsCentrifuge;
  if (missingCentrifuge) return false;
  const isDisallowedPendingNurse = !usePendingNurseID && nurseID === 'pendingNurseID';
  if (isDisallowedPendingNurse) return false;
  const isAllowedMedicalTitle = allowedTitles.includes(medicalTitle);
  if (!isAllowedMedicalTitle) return false;
  const meetsOfficeAffiliation = doesNurseMeetOfficeAffiliation(nurse, draftWaypoint);
  if (!meetsOfficeAffiliation) return false;
  const meetsOfferDeadline = doesNurseMeetOfferDeadline(nurse, timeSlotStartTime);
  if (!meetsOfferDeadline) return false;
  return true;
};

const getIsMailIn = (timeSlot, visitPlaceGroupID) => {
  var _a, _b, _c;

  return !((_c = (_b = (_a = timeSlot.placeGroups) === null || _a === void 0 ? void 0 : _a[visitPlaceGroupID]) === null || _b === void 0 ? void 0 : _b.walkIn) === null || _c === void 0 ? void 0 : _c.isOpen);
};
/**
 * Get the place group IDs associated with the lab drop-off on the
 * procedures found on this visit. For draft orders, it may be possible that the
 * lab is not yet selected. For draft orders that are ready for scheduling, a
 * lab is always expected to have been selected.
 */

const getVisitPlaceGroupIDs = params => params.draftWaypoint.draftOrderID ? [DUMMY_PLACE_GROUP_ID] : deduplicateArray(Object.values(params.procedures).map(procedure => {
  var _a;

  return Object.keys((_a = procedure.dropOffPlaceGroups) !== null && _a !== void 0 ? _a : {});
}).flat());
/**
 * Get the non-draft procedure IDs that require mail-in for a given place group.
 */

const getNonDraftMailInProcedureIDs = (params, visitPlaceGroupID) => {
  const {
    draftWaypoint,
    procedures,
    timeSlot
  } = params;
  if (draftWaypoint.draftOrderID) return [];
  return procedures.filter(procedure => procedure.id && procedure.dropOffPlaceGroups && procedure.dropOffPlaceGroups[visitPlaceGroupID] && getIsMailIn(timeSlot, visitPlaceGroupID)).map(procedure => procedure.id);
};
/**
 * Assumes time slot has already been checked and walk-in is open. This
 * only checks non-draft procedures, as draft procedures do not have supply
 * config options.
 */


const getNonDraftProceduresWalkInConfigFailure = (params, visitPlaceGroupID) => {
  const {
    options
  } = params;
  const failures = params.procedures.map(procedure => {
    var _a, _b, _c;

    const procedureID = procedure.id;
    const procedureOptions = options[procedureID];
    const walkInConfigExists = !!((_c = (_b = (_a = procedureOptions === null || procedureOptions === void 0 ? void 0 : procedureOptions.supplyInstructionsByPlaceGroup) === null || _a === void 0 ? void 0 : _a[visitPlaceGroupID]) === null || _b === void 0 ? void 0 : _b.supplyInstructionsByDeliveryType) === null || _c === void 0 ? void 0 : _c.walkIn);

    if (!walkInConfigExists) {
      return `No walk-in supply config for procedure ${procedureID} ` + `matching place group ${visitPlaceGroupID}`;
    }

    return null;
  });
  if (failures.find(nullishFilter)) return null;
  if (failures.filter(nullishFilter).length) return failures.join(', ');
  return null;
};
/**
 * Returns lowest sample stability on the mail-in option for all of the
 * (non-draft) mail-inprocedures.
 */


const getLowestMailInSampleStability = (params, visitPlaceGroupID) => {
  const {
    options
  } = params;
  const isMailIn = getIsMailIn(params.timeSlot, visitPlaceGroupID);
  if (!isMailIn) return null;
  const nonDraftMailInProcedureIDs = getNonDraftMailInProcedureIDs(params, visitPlaceGroupID);
  if (!nonDraftMailInProcedureIDs.length) return null;
  const sampleStabilities = nonDraftMailInProcedureIDs.map(procedureID => {
    var _a, _b, _c, _d, _e;

    const sampleStability = (_e = (_d = (_c = (_b = (_a = options[procedureID]) === null || _a === void 0 ? void 0 : _a.supplyInstructionsByPlaceGroup) === null || _b === void 0 ? void 0 : _b[visitPlaceGroupID]) === null || _c === void 0 ? void 0 : _c.supplyInstructionsByDeliveryType) === null || _d === void 0 ? void 0 : _d.mailIn) === null || _e === void 0 ? void 0 : _e.sampleStability;
    if (sampleStability) return sampleStability;
  }).filter(nullishFilter);
  if (!sampleStabilities.length) return null;
  return Math.min(...sampleStabilities);
};
/**
 * Based on time slot start, `estimatedAcceptanceTimestamp` and `sampleStability`,
 * determine if the time slot is compatible with mailing to the lab and being
 * accepted before samples expire.
 */


const getMailInFailure = (params, visitPlaceGroupID) => {
  try {
    const {
      labShipmentInfoByPlaceGroup,
      timeSlot
    } = params;
    const isMailIn = getIsMailIn(timeSlot, visitPlaceGroupID);
    if (!isMailIn) throw Error('No mail-in option on timeslot');
    const sampleStability = getLowestMailInSampleStability(params, visitPlaceGroupID);

    if (!sampleStability) {
      const nonDraftMailInProcedureIDs = getNonDraftMailInProcedureIDs(params, visitPlaceGroupID);
      throw Error('Missing mail-in sample stability data in supply instruction config ' + `options on at least one of the following procedures: ${nonDraftMailInProcedureIDs.join(', ')}`);
    }

    const labShipmentInfo = labShipmentInfoByPlaceGroup === null || labShipmentInfoByPlaceGroup === void 0 ? void 0 : labShipmentInfoByPlaceGroup[visitPlaceGroupID];
    if (!labShipmentInfo) throw Error('No lab shipment info found');
    const estimatedAcceptanceTimestamp = labShipmentInfo.estimatedAcceptanceTimestamp;
    if (isNullish(estimatedAcceptanceTimestamp)) throw Error('No estimatedAcceptanceTimestamp found');
    const timeSlotStart = timeSlot.startTime;
    const stabilityInMs = sampleStability * HOUR;
    const stabilityExpiry = timeSlotStart + stabilityInMs;
    const mailInWorks = estimatedAcceptanceTimestamp < stabilityExpiry;

    if (!mailInWorks) {
      const pastDeadlineMs = estimatedAcceptanceTimestamp - stabilityExpiry;
      const pastDeadline = timeInMsToHoursAndMinutes(pastDeadlineMs);
      throw Error(`Mail-in acceptance ${pastDeadline} too late.`);
    }

    return null;
  } catch (error) {
    return `Mail-in failure for place group ${visitPlaceGroupID}: ${getUnknownErrorMessage(error)}`;
  }
};
/**
 * For a given visit, we must ensure the associated place group is listed on
 * the time slot and is open (hours of operation).
 *
 * Returns true if:
 *   - Place group is included on the time slot and either `walkIn` or `mailIn`
 *     is open
 *   - For full orders, matching `walkIn` or `mailIn` is available on the
 *     supply instruction config options.
 *   - If only `mailIn` is open, given time slot `startTime`,
 *     `estimatedAcceptanceTimestamp` and `sampleStability`, the time slot must
 *     be compatible with mailing to the lab and being accepted before samples
 *     expire.
 */


const getPlaceGroupsFailure = (params, placeGroupID) => {
  const {
    timeSlot
  } = params;
  const visitPlaceGroupIDs = placeGroupID ? [placeGroupID] : getVisitPlaceGroupIDs(params);
  const failures = visitPlaceGroupIDs.map(visitPlaceGroupID => {
    var _a, _b, _c;

    const placeGroup = (_a = timeSlot.placeGroups) === null || _a === void 0 ? void 0 : _a[visitPlaceGroupID];
    if (!placeGroup) return `Place group ${visitPlaceGroupID} not available on time slot`;

    if ((_b = placeGroup.walkIn) === null || _b === void 0 ? void 0 : _b.isOpen) {
      return getNonDraftProceduresWalkInConfigFailure(params, visitPlaceGroupID);
    }

    if ((_c = placeGroup.mailIn) === null || _c === void 0 ? void 0 : _c.isOpen) {
      return getMailInFailure(params, visitPlaceGroupID);
    }

    return null;
  }).filter(nullishFilter);
  if (failures.length) return failures.join(', ');
  return null;
};

const getSamplesDeliveryType = params => {
  const {
    timeSlot
  } = params;
  const visitPlaceGroupIDs = getVisitPlaceGroupIDs(params);
  const placeGroupsFailure = getPlaceGroupsFailure(params);
  if (placeGroupsFailure) return null;
  const placeGroups = visitPlaceGroupIDs.map(visitPlaceGroupID => {
    var _a;

    const placeGroup = (_a = timeSlot.placeGroups) === null || _a === void 0 ? void 0 : _a[visitPlaceGroupID];
    return placeGroup;
  }).filter(nullishFilter);
  if (!placeGroups.length) return null;
  const somePlaceGroupIsMailIn = placeGroups.some(placeGroup => {
    var _a;

    return !((_a = placeGroup === null || placeGroup === void 0 ? void 0 : placeGroup.walkIn) === null || _a === void 0 ? void 0 : _a.isOpen);
  });
  return somePlaceGroupIsMailIn ? SamplesDeliveryType.MAIL_IN : SamplesDeliveryType.WALK_IN;
};
/**
 * Returns:
 * - `true` if at least one procedure requires centrifuge
 * - `false` if no procedures require centrifuge
 * - `null` if data is not available to determine if procedures will require
 *   centrifuge (i.e. on a draft order, or on an ineligible slot). If the slot
 *   is eligible, the visit will be treated as if no centrifuge is required.
 *
 * TODO: refactor to support multiple place groups
 */

const getRequiresCentrifugeWithoutTimeSlot = (samplesDeliveryType, params) => {
  const {
    options
  } = params;
  const optionsArray = Object.entries(options);
  if (!optionsArray.length) return null;
  let missingOptionSet = false;
  const optionsForVisitWithPlaceGroupID = optionsArray.map(([procedureID, supplyInstructions]) => {
    var _a, _b, _c;

    const procedurePlaceGroupID = (_c = (_b = (_a = params.procedures.find(procedure => procedure.id === procedureID)) === null || _a === void 0 ? void 0 : _a.dropOffPlaceGroups) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.id;
    if (!procedurePlaceGroupID) return null;
    const option = supplyInstructions.supplyInstructionsByPlaceGroup[procedurePlaceGroupID];

    if (!option) {
      missingOptionSet = true;
      return null;
    }

    return {
      option,
      procedurePlaceGroupID
    };
  }).filter(nullishFilter);
  if (missingOptionSet) return null;
  const requiresCentrifuge = optionsForVisitWithPlaceGroupID.some(optionSet => {
    var _a;

    const option = optionSet.option.supplyInstructionsByDeliveryType[samplesDeliveryType];
    return !!((_a = option === null || option === void 0 ? void 0 : option.processingIncludes) === null || _a === void 0 ? void 0 : _a.centrifuge);
  });
  return requiresCentrifuge;
};
/**
 * Returns:
 * - `true` if at least one procedure requires centrifuge
 * - `false` if no procedures require centrifuge
 * - `null` if data is not available to determine if procedures will require
 *   centrifuge (i.e. on a draft order, or on an ineligible slot). If the slot
 *   is eligible, the visit will be treated as if no centrifuge is required.
 *
 * TODO: refactor to support multiple place groups
 */

const getRequiresCentrifuge = params => {
  const {
    options
  } = params;
  const optionsArray = Object.entries(options);
  if (!optionsArray.length) return null;
  const samplesDeliveryType = getSamplesDeliveryType(params);
  if (!samplesDeliveryType) return null;
  let missingOptionSet = false;
  const optionsForVisit = optionsArray.map(([procedureID, supplyInstructions]) => {
    var _a, _b, _c;

    const procedurePlaceGroupID = (_c = (_b = (_a = params.procedures.find(procedure => procedure.id === procedureID)) === null || _a === void 0 ? void 0 : _a.dropOffPlaceGroups) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.id;
    if (!procedurePlaceGroupID) return null;
    const optionSet = supplyInstructions.supplyInstructionsByPlaceGroup[procedurePlaceGroupID];

    if (!optionSet) {
      missingOptionSet = true;
      return null;
    }

    return optionSet;
  }).filter(nullishFilter);
  if (missingOptionSet) return null;
  const requiresCentrifuge = optionsForVisit.some(optionSet => {
    var _a;

    const option = optionSet.supplyInstructionsByDeliveryType[samplesDeliveryType];
    return !!((_a = option === null || option === void 0 ? void 0 : option.processingIncludes) === null || _a === void 0 ? void 0 : _a.centrifuge);
  });
  return requiresCentrifuge;
};
/**
 * Check if a given nurse has the required supplies.
 */


const getNurseHasSupplies = (nurse, requiredSupplies) => {
  if (nurse.allSupplies) return true;
  if (!nurse.supplies) return false;
  const nurseSupplyIDs = Object.keys(nurse.supplies);
  const notAllSuppliesMet = requiredSupplies.some(supplyID => !nurseSupplyIDs.includes(supplyID));
  return !notAllSuppliesMet;
};
/**
 * Return all eligible nurses on a timeslot. The nurses must must meet the
 * following criteria:
 * - Active
 * - Owns a centrifuge if required
 * - Has the required medical title
 * - Is affiliated with the required offices
 * - Meets the offer deadline
 *
 * Note: In some cases we need to separately check if the nurse has all of the
 * required supplies. This is required if the
 * `SchedulingFilter.SUPPLIES_SHIPPED_BY_CARESEND` filter is active, and we
 * expected a nurse to have all of the required supplies.
 */

const getEligibleNurses = (params, minimumTitle, usePendingNurseID) => {
  const {
    draftWaypoint,
    timeSlot
  } = params;
  const {
    availableNurses
  } = timeSlot;
  const requiresCentrifuge = getRequiresCentrifuge(params);
  const allowedTitles = getAllowedMedicalTitles(minimumTitle);
  const eligibleNurses = objectFilter(availableNurses, nurse => isValidNurse(nurse, draftWaypoint, timeSlot.startTime, requiresCentrifuge !== null && requiresCentrifuge !== void 0 ? requiresCentrifuge : false, usePendingNurseID !== null && usePendingNurseID !== void 0 ? usePendingNurseID : false, allowedTitles));
  return eligibleNurses;
};
/**
 * Updates the ranking on all nurse availabilities based on the labor cost.
 * The logic in here may change in the future.
 */

const getRankedNurseAvailabilities = allNurseAvailabilities => {
  const rankedNurseAvailabilities = {};
  const sortedAvailabilities = Object.values(allNurseAvailabilities).sort((a, b) => a.laborCost - b.laborCost);
  sortedAvailabilities.forEach((nurseAvailability, index) => {
    rankedNurseAvailabilities[nurseAvailability.nurseID] = Object.assign(Object.assign({}, nurseAvailability), {
      rank: index
    });
  });
  return rankedNurseAvailabilities;
};
/**
 * Gets the best eligible nurse (e.g. highest ranked) for a given time slot.
 * Only call this function once the nurses have been ranked on the timeslot.
 */

const getBestEligibleNurse = (eligibleNurses, preferredOfficeIDs) => {
  const eligibleNursesArray = Object.values(eligibleNurses);
  if (!eligibleNursesArray.length) return;
  const rankedNurses = eligibleNursesArray.sort((a, b) => a.rank - b.rank);
  if (!(preferredOfficeIDs === null || preferredOfficeIDs === void 0 ? void 0 : preferredOfficeIDs.length)) return rankedNurses[0];
  const bestNurse = rankedNurses.find(nurse => preferredOfficeIDs.includes(nurse.staffingOfficeID));
  return bestNurse;
};
const sortNursesByPreferredStaffingOffices = (availableNurses, preferredOffices) => Object.values(availableNurses).sort((a, b) => {
  const aIsPreferred = preferredOffices === null || preferredOffices === void 0 ? void 0 : preferredOffices.includes(a.staffingOfficeID);
  const bIsPreferred = preferredOffices === null || preferredOffices === void 0 ? void 0 : preferredOffices.includes(b.staffingOfficeID);
  if (aIsPreferred && !bIsPreferred) return -1;
  if (!aIsPreferred && bIsPreferred) return 1;
  return 0;
});
const getArrayOfAllTimeSlots = timeSlots => Object.values(timeSlots).flatMap(timeSlotsByDate => Object.values(timeSlotsByDate));
/**
 * Returns timeslots that are not disabled. This is useful during debug mode
 * when all timeslots get returned, but with the disabled flag set if
 * ineligible.
 */

const getEligibleTimeSlots = timeSlotsByDate => {
  const eligibleTimeSlotsByDate = {};
  Object.entries(timeSlotsByDate).forEach(([date, timeSlots]) => {
    Object.entries(timeSlots).forEach(([time, timeSlot]) => {
      if (!timeSlot.disabled) {
        eligibleTimeSlotsByDate[date] = Object.assign(Object.assign({}, eligibleTimeSlotsByDate[date]), {
          [time]: timeSlot
        });
      }
    });
  });
  return eligibleTimeSlotsByDate;
};
/**
 * Returns true if the nurse is from the integration name provided.
 * If no integration name is provided, returns true if the nurse is from any
 * integration.
 */

const isIntegrationNurse = (nurse, integrationName) => {
  var _a;

  const nurseIntegrationName = (_a = nurse.integration) === null || _a === void 0 ? void 0 : _a.name;
  return integrationName ? nurseIntegrationName === integrationName : !!nurseIntegrationName;
};
/**
 * Returns timeslots that have nurses from the integration. Useful for debugging
 */

const getTimeSlotsWithIntegrationNurses = (timeSlots, integrationName) => Object.values(timeSlots).reduce((acc, slotsByDate) => {
  Object.values(slotsByDate).forEach(timeSlot => {
    const {
      availableNurses
    } = timeSlot;
    const hasIntegrationNurse = objectSome(availableNurses, nurse => isIntegrationNurse(nurse, integrationName));

    if (hasIntegrationNurse) {
      acc[timeSlot.startDate] = Object.assign(Object.assign({}, acc[timeSlot.startDate]), {
        [timeSlot.startTime]: timeSlot
      });
    }
  });
  return acc;
}, {});
/**
 * Removes the timezone from the start date times.
 *
 * ExamOne returns times with timezone offsets, but they explicitly state that
 * "Any time zone indicators or time zone offsets shall be ignored. All times
 * shall be assumed to be for the zone of the appointment location for the given
 * date." This means the times are already correct and we need to strip the
 * offset without adjusting the time values.
 */

const removeTimezoneFromStartDateTimes = startDateTimes => startDateTimes.map(time => {
  // Remove timezone offset (everything after + or - or Z at the end)
  // This preserves the exact time values without any adjustment
  const timeWithoutOffset = time.replace(/[+-]\d{2}:\d{2}$|Z$/, '');
  return timeWithoutOffset;
});
/**
 * Gets the eligible appointment times for an ExamOne visit.
 *
 * @param startTime - The start time of the visit in milliseconds since epoch
 * @param endTime - The end time of the visit in milliseconds since epoch (exclusive)
 * @param examOneTimes - An array of appointment times that are available for in
 *   ISO 8601 format without timezones.
 * @param timezone - The timezone of the visit (e.g., 'America/New_York')
 * @returns An array of appointment times that are eligible for an ExamOne
 *   appointment, meaning the appointment time is >= startTime and < endTime.
 */

const getEligibleExamOneAppointmentTimes = (startTime, endTime, examOneTimes, timezone) => examOneTimes.filter(examOneTime => {
  // Parse the ISO string without timezone as a time in the specified timezone
  const appointmentTime = dayjs.tz(examOneTime, timezone);
  const appointmentTimeMs = appointmentTime.valueOf(); // Check if the appointment time falls within the start and end time window,
  // but not on the end time.

  return appointmentTimeMs >= startTime && appointmentTimeMs < endTime;
});
/**
 * Checks if the start time adheres to the collection date (info stems from procedure)
 */

const checkProcedureCollectionDateForTimeSlot = (startTime, timeZone, collectOnTimestamp, collectEndTimestamp, collectionDate) => {
  if (!collectionDate || !collectOnTimestamp) return true;
  const startOf = changeTimeZone(collectOnTimestamp, timeZone);
  const startOfCollectionDay = dayjs(startOf.timestamp).tz(startOf.timeZone).startOf('day');
  const endOfCollectionDay = dayjs(startOf.timestamp).tz(startOf.timeZone).endOf('day');
  const startTimeDayjs = dayjs(startTime).tz(timeZone);
  const betweenRangeEndTzDate = changeTimeZone(collectEndTimestamp !== null && collectEndTimestamp !== void 0 ? collectEndTimestamp : 0, timeZone);
  const betweenRangeEnd = dayjs(betweenRangeEndTzDate.timestamp).tz(betweenRangeEndTzDate.timeZone).endOf('day');

  switch (collectionDate) {
    case CollectionDate.ANY:
      return true;

    case CollectionDate.ON:
      return startTimeDayjs.isAfter(startOfCollectionDay) && startTimeDayjs.isBefore(endOfCollectionDay);

    case CollectionDate.BEFORE:
      return startTimeDayjs.isBefore(startOfCollectionDay);

    case CollectionDate.AFTER:
      return startTimeDayjs.isAfter(endOfCollectionDay);

    case CollectionDate.BETWEEN:
      return startTimeDayjs.isAfter(startOfCollectionDay) && startTimeDayjs.isBefore(betweenRangeEnd);
  }
};
/**
 * Checks if the start time adheres to the collection time (info stems from procedure)
 */

const checkProcedureCollectionTimeForTimeSlot = (startTime, timeZone, collectionTime) => {
  if (!collectionTime) return true;
  const startOfDay = dayjs(startTime).tz(timeZone).startOf('day');
  const startTimeDayjs = dayjs(startTime).tz(timeZone);
  const endOfDay = startOfDay.endOf('day');
  const collectOnDateNoon = startOfDay.add(12, 'hours');

  switch (collectionTime) {
    case CollectionTime.ANY:
      return true;

    case CollectionTime.AM:
      return collectOnDateNoon.isAfter(startTime);

    case CollectionTime.PM:
      return collectOnDateNoon.isBefore(startTimeDayjs) && startTimeDayjs.isBefore(endOfDay);
  }
};
/**
 * Validates that all procedures meet their collection date and time restrictions
 * for the given time slot. Optimized for performance with early exit strategy.
 *
 * @param procedures - Map of procedures by ID with their collection requirements
 * @param startTime - Start time of the appointment in milliseconds
 * @param timeZone - Timezone for date calculations
 * @returns true if all procedures meet their restrictions, false otherwise
 */

const getMeetsCollectionRestrictions = (procedures, startTime, timeZone) => {
  // Use for...in loop for better performance than objectSome
  for (const procedureId in procedures) {
    const procedure = procedures[procedureId];
    if (!procedure) continue;
    const {
      collectionDate,
      collectionTime,
      collectOn,
      collectEnd
    } = procedure;

    if (!collectionDate && !collectionTime && !collectOn && !collectEnd) {
      continue;
    }

    if (collectionDate || collectOn || collectEnd) {
      const isValidCollectionDate = checkProcedureCollectionDateForTimeSlot(startTime, timeZone, collectOn === null || collectOn === void 0 ? void 0 : collectOn.timestamp, collectEnd === null || collectEnd === void 0 ? void 0 : collectEnd.timestamp, collectionDate);
      if (!isValidCollectionDate) return false;
    }

    if (collectionTime) {
      const isValidCollectionTime = checkProcedureCollectionTimeForTimeSlot(startTime, timeZone, collectionTime);
      if (!isValidCollectionTime) return false;
    }
  }

  return true;
};
/**
 * Converts the time slot restrictions to milliseconds from start of day. This
 * helps us easily compare the time to the start of the day.
 */


const convertTimeSlotRestrictions = timeSlotRestrictions => {
  if (!timeSlotRestrictions) return undefined;
  return Object.entries(timeSlotRestrictions).reduce((acc, [day, restrictions]) => {
    const {
      startTime,
      endTime
    } = restrictions;
    const startHour = Math.floor(startTime / 100);
    const startMinute = startTime % 100;
    const endHour = Math.floor(endTime / 100);
    const endMinute = endTime % 100;
    const startTimeOffset = (startHour * 60 + startMinute) * MINUTE;
    const endTimeOffset = (endHour * 60 + endMinute) * MINUTE;
    acc[day] = {
      startInMsFromStartOfDay: startTimeOffset,
      endInMsFromStartOfDay: endTimeOffset
    };
    return acc;
  }, {});
};
/**
 * Calculates the cutoff date for the lead time filter by adding the required
 * business days to the start date.
 * Business days are weekdays (Monday-Friday), weekends are skipped.
 *
 * @param startDate - The starting date (today)
 * @param leadTimeInDays - The required number of business days lead time
 * @returns The earliest date that satisfies the lead time requirement
 */


const getLeadTimeCutoffTimestamp = (startDate, labTimeZone, leadTimeInDays) => {
  const start = dayjs.tz(startDate, labTimeZone).startOf('day');
  let businessDaysAdded = 0;
  let currentDate = start.add(1, 'day');

  while (businessDaysAdded < leadTimeInDays) {
    const dayOfWeek = currentDate.day();
    const isWeekend = dayOfWeek === SUNDAY || dayOfWeek === SATURDAY;
    if (!isWeekend) businessDaysAdded++;

    if (businessDaysAdded < leadTimeInDays) {
      currentDate = currentDate.add(1, 'day');
    }
  } // We purposefully get the lead time using the start of the day


  return currentDate.startOf('day').valueOf();
};
/**
 * Gets the lowest sample stability for a given visit using mail-in sample stability.
 * Used to determine if Friday visits should be restricted due to weekend processing delays.
 *
 * @param supplyInstructions - Configuration for supply delivery and sample stability
 * @returns The lowest sample stability in hours, or null if no stability data found
 */


const getLowestSampleStabilityForLabVisit = supplyInstructions => {
  var _a;

  const sampleStabilities = [];

  for (const supplyInstruction of Object.values(supplyInstructions)) {
    const placeGroupInstructions = Object.values((_a = supplyInstruction.supplyInstructionsByPlaceGroup) !== null && _a !== void 0 ? _a : {});
    if (!placeGroupInstructions.length) continue;

    for (const placeGroup of placeGroupInstructions) {
      const mailInConfig = placeGroup.supplyInstructionsByDeliveryType.mailIn;

      if (mailInConfig === null || mailInConfig === void 0 ? void 0 : mailInConfig.sampleStability) {
        sampleStabilities.push(mailInConfig.sampleStability);
      }
    }
  }

  const lowestSampleStability = sampleStabilities.length ? Math.min(...sampleStabilities) : null;
  return lowestSampleStability;
};
/**
 * Gets federal holidays for the current and next year as a Set of date strings.
 * Optimized for O(1) lookup performance during date filtering.
 *
 * @param currentDate - The current date to determine which years to include
 * @returns Set of holiday date strings in 'YYYY-MM-DD' format
 */


const getFederalHolidaysSet = currentDate => {
  const currentYear = currentDate.getFullYear();
  const years = [currentYear, currentYear + 1];
  const holidayStrings = [];
  holidayStrings.length = Holidays.all.length * years.length;
  let holidayIndex = 0;

  for (const year of years) {
    for (const holiday of Holidays.all) {
      const holidayDate = holiday.getter(year); // The us-holidays library returns UTC dates, but we need to treat them as calendar dates
      // Extract the UTC calendar components directly to avoid timezone conversion overhead

      const utcYear = holidayDate.getUTCFullYear();
      const utcMonth = String(holidayDate.getUTCMonth() + 1).padStart(2, '0');
      const utcDay = String(holidayDate.getUTCDate()).padStart(2, '0'); // Create date string directly without intermediate Date object

      holidayStrings[holidayIndex++] = `${utcYear}-${utcMonth}-${utcDay}`;
    }
  }

  return new Set(holidayStrings);
};
/**
 * Checks if a given date is a federal holiday using optimized Set lookup.
 *
 * @param dayJsDate - The dayjs date object to check
 * @param federalHolidaySet - Pre-computed Set of holiday date strings
 * @returns true if the date is a federal holiday, false otherwise
 */


const getIsFederalHoliday = (dayJsDate, federalHolidaySet) => {
  // Format date as YYYY-MM-DD for Set lookup
  const year = dayJsDate.year();
  const month = String(dayJsDate.month() + 1).padStart(2, '0');
  const day = String(dayJsDate.date()).padStart(2, '0');
  const dateString = `${year}-${month}-${day}`;
  return federalHolidaySet.has(dateString);
};
/**
 * Determines if any supply instruction has short sample stability (< 72 hours).
 * This is used to restrict Friday visits when samples would degrade over weekends.
 *
 * @param lowestSampleStability - The lowest sample stability in hours, or null/undefined
 * @returns true if sample stability is less than 72 hours, false otherwise
 */


const getHasShortSampleStability = lowestSampleStability => {
  if (isNullish(lowestSampleStability)) return false;
  return lowestSampleStability < SAMPLE_STABILITY_HOURS;
};
/**
 * Checks if the lab is open on the given day of week.
 * Optimized to use Set lookup for O(1) performance.
 *
 * @param dayOfWeek - Day of week (0=Sunday, 1=Monday, etc.)
 * @param daysOpenSet - Set of days when lab is open, or null if always open
 * @returns true if lab is open on the given day, false otherwise
 */


const getIsLabOpen = (dayOfWeek, daysOpenSet) => {
  if (!daysOpenSet) return true;
  return daysOpenSet.has(dayOfWeek);
};
/**
 * Gets the earliest timestamp for a lab visit could be eligible. Excludes other
 * filters like weekends, federal holidays, etc.
 */


const getCutoffTimestamp = (currentDate, supplyInstructions, leadTimeInDays, labTimeZone) => {
  const timestampSuppliesArePresent = getLatestSupplyArrivalTimestamp(supplyInstructions);
  const leadTimeCutoffTimestamp = getLeadTimeCutoffTimestamp(currentDate, labTimeZone, leadTimeInDays);
  /** The earliest timestamp for a lab visit */

  const cutoffTimestamp = Math.max(leadTimeCutoffTimestamp, timestampSuppliesArePresent);
  return cutoffTimestamp;
};
/**
 * Checks if a given day of week meets the day restrictions.
 */


const getWeekdayMeetsDayRestrictions = (day, dayName, timeSlotRestrictions) => {
  const restrictions = timeSlotRestrictions === null || timeSlotRestrictions === void 0 ? void 0 : timeSlotRestrictions[dayName];
  if (!restrictions) return true;
  const milliseconds = day.valueOf();
  const startOfDayInMilliseconds = day.startOf('day').valueOf();
  const {
    startInMsFromStartOfDay,
    endInMsFromStartOfDay
  } = restrictions;
  const timeFromStartOfDay = milliseconds - startOfDayInMilliseconds;
  const meetsStartRestriction = timeFromStartOfDay >= startInMsFromStartOfDay;
  if (!meetsStartRestriction) return false;
  const meetsEndRestriction = timeFromStartOfDay <= endInMsFromStartOfDay;
  if (!meetsEndRestriction) return false;
  return true;
};
/**
 * Filters a list of potential lab visit dates to return only those that
 * meet all scheduling requirements.
 *
 * This function applies comprehensive validation criteria to determine which
 * dates are valid for lab visits. Each date is evaluated against multiple
 * business rules and operational constraints:
 *
 * **Temporal Requirements:**
 * - Lead time requirements: Ensures sufficient business days between today
 *   and the visit date (excludes weekends from business day calculation)
 * - Supply arrival timing: Ensures supplies have arrived before the visit
 *
 * **Operational Hours & Availability:**
 * - Lab operating hours: Checks if the lab is open on the given date
 * - Weekend filtering: Optionally excludes weekend dates (Saturday/Sunday)
 * - Federal holiday exclusion: Automatically excludes US federal holidays
 *   using the us-holidays package (includes all major federal holidays)
 *
 * **Special Day Restrictions:**
 * - Timeslot restrictions: Applies special time window restrictions for
 *   appointment times and days of the week when specified
 * - Sample stability: Prevents Friday visits when samples have short
 *   stability periods (typically <72 hours) to avoid degradation over weekends
 *
 * **Procedure-Specific Validation:**
 * - Collection restrictions: Validates against procedure-specific collection
 *   date/time requirements and constraints
 *
 * @param dates - Array of potential visit dates (already in lab's timezone)
 * @param labTimeZone - Time zone for date calculations
 * @param daysOpen - Array of day indices (0=Sunday, 6=Saturday) when the lab
 *   is open, or null if always open
 * @param leadTimeInDays - Required number of business days lead time before visit
 * @param procedures - Map of procedures by ID with their collection requirements
 * @param supplyInstructions - Configuration for supply delivery and sample
 *   stability requirements
 * @param filterWeekend - Optional flag to exclude weekend dates
 * @param timeSlotRestrictions - Optional time window restrictions for any day
 *   appointments (e.g., 10 AM - 2 PM in military time)
 * @returns Array of valid visit dates that meet all requirements
 */


const getValidLabVisitTimes = (dates, labTimeZone, daysOpen, leadTimeInDays, procedures, supplyInstructions, filterWeekend, timeSlotRestrictions) => {
  if (!dates.length) return [];
  const currentDate = new Date(); // Pre-compute all expensive calculations once

  const lowestSampleStability = getLowestSampleStabilityForLabVisit(supplyInstructions);
  const hasShortSampleStability = getHasShortSampleStability(lowestSampleStability);
  const timeSlotRestrictionsConverted = convertTimeSlotRestrictions(timeSlotRestrictions);
  const cutoffTimestamp = getCutoffTimestamp(currentDate, supplyInstructions, leadTimeInDays, labTimeZone);
  const federalHolidaySet = getFederalHolidaysSet(currentDate);
  const daysOpenSet = (daysOpen === null || daysOpen === void 0 ? void 0 : daysOpen.length) ? new Set(daysOpen) : null;
  const hasProcedureRestrictions = Object.values(procedures).some(proc => (proc === null || proc === void 0 ? void 0 : proc.collectionDate) || (proc === null || proc === void 0 ? void 0 : proc.collectionTime) || (proc === null || proc === void 0 ? void 0 : proc.collectOn) || (proc === null || proc === void 0 ? void 0 : proc.collectEnd));
  return dates.filter(date => {
    // Always interpret the date in the lab's timezone
    const dayJsDate = dayjs.tz(date, labTimeZone);
    const dayOfWeek = dayJsDate.day();
    const milliseconds = dayJsDate.valueOf();
    const dayName = getDayOfWeekFromDayJS(dayJsDate);
    const isFriday = dayOfWeek === FRIDAY;
    const isWeekend = dayOfWeek === SATURDAY || dayOfWeek === SUNDAY;
    const isNotAllowedWeekend = filterWeekend && isWeekend;
    if (isNotAllowedWeekend) return false;
    const isNotOpen = !getIsLabOpen(dayOfWeek, daysOpenSet);
    if (isNotOpen) return false;
    const isNotAllowedFriday = isFriday && hasShortSampleStability;
    if (isNotAllowedFriday) return false;
    const isDateTooEarly = milliseconds < cutoffTimestamp;
    if (isDateTooEarly) return false;
    const meetsDayRestrictions = getWeekdayMeetsDayRestrictions(dayJsDate, dayName, timeSlotRestrictionsConverted);
    if (!meetsDayRestrictions) return false;
    const isFederalHoliday = getIsFederalHoliday(dayJsDate, federalHolidaySet);
    if (isFederalHoliday) return false;
    if (!hasProcedureRestrictions) return true;
    const meetsCollectionRestrictions = getMeetsCollectionRestrictions(procedures, milliseconds, labTimeZone);
    return meetsCollectionRestrictions;
  });
};

export { checkProcedureCollectionDateForTimeSlot, checkProcedureCollectionTimeForTimeSlot, getArrayOfAllTimeSlots, getBestEligibleNurse, getEligibleExamOneAppointmentTimes, getEligibleNurses, getEligibleTimeSlots, getIsMailIn, getNurseHasSupplies, getRankedNurseAvailabilities, getRequiresCentrifugeWithoutTimeSlot, getSamplesDeliveryType, getTimeSlotPath, getTimeSlotsWithIntegrationNurses, getValidLabVisitTimes, getVisitPlaceGroupIDs, isIntegrationNurse, removeTimezoneFromStartDateTimes, sortNursesByPreferredStaffingOffices };
